import type { Product, ProductCategory, Routine, SkinType, Treatment, TreatmentCategory } from '../../core/types/catalog'
import type { Article, ArticleCategory } from '../../core/types/content'
import { skinTypes } from '../../data/concerns'
import { treatmentCategories } from '../../data/treatments'
import { fullDate } from '../../core/format/dates'
import type { Check } from '../editing'
import { mediaProblem } from '../../core/rules/media'
import { LASTS_WEEKS_MAX, LASTS_WEEKS_MIN, isValidLastsWeeks, productLastsWeeks } from '../../core/rules/usage'

/**
 * Regels voor de catalogus-editors in het beheer (product, protocol, behandeling,
 * artikel): lege sjablonen, keuzelijsten en de controles "wat mist er nog?".
 * Puur, zonder React.
 */

// ——— Keuzelijsten ———

export const productCategories: readonly ProductCategory[] = ['Reiniging', 'Toner', 'Serum', 'Olie', 'Crème', 'Masker', 'SPF']
/** Eén lijst voor webshop en beheer. */
export { routineSteps } from '../../data/concerns'
export const routineLevels: readonly Routine['level'][] = ['Start', 'Compleet']
export const articleCategories: readonly ArticleCategory[] = ['Producten', 'Huidkennis', 'Energetisch werk']

/**
 * De waarde van een keuzelijst terug als het juiste type, of `undefined` als hij
 * er niet in staat. Zo hoeft een `<select>` zijn waarde nooit blind om te zetten.
 */
export const oneOf = <T extends string>(options: readonly T[], value: string): T | undefined =>
  options.find((o) => o === value)

export const asSkinType = (value: string): SkinType | undefined => oneOf(skinTypes, value)
export const asTreatmentCategory = (value: string): TreatmentCategory | undefined => oneOf(treatmentCategories, value)

// ——— Grenzen ———

/** Langer past niet netjes op een productkaart. */
export const PRODUCT_NOTE_MAX = 110
/** Zoveel tekens toont Google ongeveer van een samenvatting. */
export const EXCERPT_MAX = 160
/** Langer is geen samenvatting meer, maar een eerste alinea. */
const EXCERPT_WARN = 240
/** Zoveel suggesties onder een tag-veld (ingrediënten, focus). */
export const TAG_SUGGESTIONS = 8
/** Gemiddelde leessnelheid voor de geschatte leestijd. */
const WORDS_PER_MINUTE = 200
/** Een kuur heeft minstens twee sessies; zo begint een nieuwe kuur. */
export const KUUR_MIN_SESSIONS = 2
export const KUUR_DEFAULT_SESSIONS = 6

// ——— Lege sjablonen voor "nieuw" ———

export const emptyProduct = (): Product => ({
  id: '',
  name: '',
  brand: 'emerginC',
  category: 'Serum',
  step: 'Behandelen',
  price: 0,
  vatRate: 21,
  image: '',
  skinTypes: [],
  note: '',
  why: '',
  howTo: '',
  keyIngredients: [],
  rating: 0,
  reviews: 0,
  refill: true,
  pairs: [],
})

export const emptyRoutine = (): Routine => ({
  id: '',
  name: '',
  claim: '',
  story: '',
  forWho: '',
  productIds: [],
  included: ['Persoonlijk huidplan in Mijn omgeving', 'Opbouwschema voor de eerste weken'],
  level: 'Start',
  lasts: { minMonths: 3, maxMonths: 3 },
  image: '',
})

export const emptyTreatment = (): Treatment => ({
  slug: '',
  name: '',
  tagline: '',
  description: '',
  durationMinutes: 60,
  price: 0,
  focus: [],
  category: 'Huidverbetering',
})

export const emptyArticle = (): Article => ({
  slug: '',
  title: '',
  excerpt: '',
  category: 'Huidkennis',
  readTime: '4 min',
  date: fullDate(new Date()),
  productIds: [],
  body: [{ type: 'p', text: '' }],
})

// ——— Afgeleid ———

/** Alle waarden uit een lijst van lijsten, uniek en op volgorde (bijv. alle ingrediënten). */
export const uniqueSorted = (lists: string[][]) => Array.from(new Set(lists.flat())).sort()

/** Woorden in een artikel en de geschatte leestijd: "4 min". */
export function readingTime(a: Article) {
  const words = a.body
    .map((b) => (b.type === 'list' ? b.items.join(' ') : b.text))
    .join(' ')
    .split(/\s+/)
    .filter(Boolean).length
  return { words, estimate: `${Math.max(1, Math.round(words / WORDS_PER_MINUTE))} min` }
}

// ——— Controles: wat mist er nog? ———

export { errorCount, type Check } from '../editing'

export function productChecks(p: Product): Check[] {
  const checks: Check[] = []
  if (!p.name.trim()) checks.push({ level: 'error', text: 'Naam ontbreekt' })
  if (!p.image) checks.push({ level: 'error', text: 'Productfoto ontbreekt' })
  if (p.price <= 0) checks.push({ level: 'error', text: 'Prijs staat op € 0' })
  if (p.kind !== 'giftcard' && p.skinTypes.length === 0)
    checks.push({ level: 'error', text: 'Geen huidthema: het product verschijnt nergens in advies of filters' })
  if (!p.note.trim()) checks.push({ level: 'warning', text: 'Korte omschrijving ontbreekt' })
  if (!p.volume) checks.push({ level: 'warning', text: 'Inhoud (bijv. 30 ml) nog niet ingevuld' })
  if (p.kind !== 'giftcard') {
    if (p.lastsWeeks !== undefined && !isValidLastsWeeks(p.lastsWeeks))
      checks.push({ level: 'error', text: `Gebruiksduur moet een heel aantal weken zijn (${LASTS_WEEKS_MIN}–${LASTS_WEEKS_MAX})` })
    else if (productLastsWeeks(p) === null)
      checks.push({ level: 'warning', text: 'Gebruiksduur nog niet ingevuld: bij bijvullen gaat het product dan in elke doos mee' })
  }
  if (p.note.length > PRODUCT_NOTE_MAX) checks.push({ level: 'warning', text: 'Korte omschrijving is lang voor een productkaart' })
  ;(p.media ?? []).forEach((m, i) => {
    const problem = mediaProblem(m)
    if (problem) checks.push({ level: 'error', text: `Galerij, beeld ${i + 1}: ${problem}` })
  })
  return checks
}

export function routineChecks(r: Routine): Check[] {
  const checks: Check[] = []
  if (!r.name.trim()) checks.push({ level: 'error', text: 'Naam ontbreekt' })
  if (r.productIds.length < 2) checks.push({ level: 'error', text: 'Een protocol heeft minstens twee producten' })
  if (!r.image) checks.push({ level: 'warning', text: 'Sfeerbeeld ontbreekt' })
  if (r.lasts.minMonths > r.lasts.maxMonths) checks.push({ level: 'error', text: 'Minimale duur is langer dan de maximale' })
  return checks
}

export function articleChecks(a: Article): Check[] {
  const checks: Check[] = []
  if (!a.title.trim()) checks.push({ level: 'error', text: 'Titel ontbreekt' })
  if (!a.excerpt.trim()) checks.push({ level: 'warning', text: 'Samenvatting ontbreekt — die zie je in Google en op het overzicht' })
  if (a.excerpt.length > EXCERPT_WARN)
    checks.push({ level: 'warning', text: `Samenvatting is lang voor Google (max. ± ${EXCERPT_MAX} tekens zichtbaar)` })
  if (!a.image) checks.push({ level: 'warning', text: 'Geen beeld: het artikel toont dan alleen tekst' })
  if (a.body.every((b) => (b.type === 'list' ? b.items.length === 0 : !b.text.trim())))
    checks.push({ level: 'error', text: 'Het artikel heeft nog geen tekst' })
  return checks
}
